/**
 * Stage 2 decisions — shared vocabulary.
 *
 * When early screening (Stage 1) completes, the site's next work is mature
 * validation (Stage 2), which needs design and delivery decisions the
 * screening brief deliberately did not ask for. This module family turns the
 * Stage 1 findings plus the frozen site evidence into a checklist of those
 * decisions, each with the evidence for and against every option.
 *
 * Invariants (see docs/plan/two-stage-intent-workflow-2026-09-28.md §1):
 *   - A proposal is never written without an explicit user action.
 *   - A user-authored or recorded-unknown answer is never overwritten.
 *   - A stance is screening evidence, not an adopted design or approval.
 *   - Commercial terms (offtake/PPA) are recorded, never proposed.
 *   - `excluded-by-evidence` is reserved for a binding, evidenced prohibition;
 *     regulatory difficulty or cost alone is at most `constrained`.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type {
  BopHeatRejection,
  GridConnectionMode,
  ProjectIntent,
} from '../../types/siteRag';
import type { FindingClaim } from '../findingClaim';
import { stripNarrativeArtifacts } from '../reportRendering/narrativeArtifacts';
import type { PowerOptionId } from './stage2Evidence';
import type { Stage2SourceId } from './stage2Sources';

export type Stage2DecisionId =
  | 'power-supply'
  | 'cooling'
  | 'build-phases'
  | 'design-basis'
  | 'firm-capacity'
  | 'water-supply'
  | 'grid-path'
  | 'fuel-supply'
  | 'nuclear'
  | 'commercial';

export type Stage2DecisionStatus =
  | 'open'
  | 'decided'
  | 'recorded-unknown'
  | 'deferred'
  | 'not-applicable';

/** Most restrictive first — the aggregation order used by `combineStances`. */
export type OptionStance =
  | 'excluded-by-evidence'
  | 'constrained'
  | 'viable'
  | 'favoured'
  | 'not-assessed';

/**
 * Why a reason exists. `screening-policy` marks a threshold that is platform
 * judgement rather than a regulation, a measured site fact or a cited study.
 */
export type StanceBasis =
  | 'regulation'
  | 'site-evidence'
  | 'screening-report'
  | 'industry-evidence'
  | 'screening-policy'
  | 'owner-input';

export interface StanceReason {
  text: string;
  basis: StanceBasis;
  /**
   * The stance this reason supports. Informational reasons use 'viable'; a
   * reason that only explains why the option is not assessed (no evidence,
   * regional context) uses 'not-assessed' and never makes the option viable.
   */
  effect: OptionStance;
  sourceIds?: readonly Stage2SourceId[];
  /** SED path or `report:<id>.<field>` the evidence came from. */
  evidencePath?: string;
  /**
   * Names a rule that other logic keys on. 'ozone-transport': the Ozone
   * Transport Region reason, which rests on location, not on a designation.
   */
  rule?: 'ozone-transport';
}

export interface Stage2OptionAssessment {
  optionId: string;
  label: string;
  stance: OptionStance;
  reasons: StanceReason[];
}

export interface Stage2Fact {
  label: string;
  value: string;
  evidencePath?: string;
}

export interface Stage2OpenQuestion {
  question: string;
  followUp?: string;
  reportType?: ReportTypeId;
  reportId?: string;
}

export type BtmStrategy = 'grid-only' | 'btm-only' | 'hybrid' | 'islanded';

/** A one-click proposal. Applying one is always an explicit user action. */
export type Stage2Proposal =
  | {
      kind: 'btm-strategy';
      value: BtmStrategy;
      summary: string;
      reasons: StanceReason[];
    }
  | {
      kind: 'phase-power';
      phases: Array<{
        phaseId: string;
        phaseLabel: string;
        /** Supply options named by the screening pathway for this phase. */
        options: PowerOptionId[];
        /** The pathway also names battery storage for this phase. */
        battery: boolean;
        gridConnection: GridConnectionMode;
      }>;
      summary: string;
      reasons: StanceReason[];
    }
  | {
      kind: 'cooling';
      bopHeatRejection?: Exclude<BopHeatRejection, 'unknown'>;
      coolingStrategy?: Exclude<ProjectIntent['coolingStrategy'], 'undecided'>;
      summary: string;
      reasons: StanceReason[];
    };

export interface Stage2Decision {
  id: Stage2DecisionId;
  label: string;
  question: string;
  status: Stage2DecisionStatus;
  /** Shapes the Generation & Impact scenario; reviewed before promotion. */
  scenarioShaping: boolean;
  neededBy: readonly ReportTypeId[];
  /** `data-field-id` the Design view focuses for this decision. */
  focusField: string;
  /** Options with stances — only for decisions that compare options. */
  options: Stage2OptionAssessment[];
  facts: Stage2Fact[];
  openQuestions: Stage2OpenQuestion[];
  proposals: Stage2Proposal[];
  /** An accepted proposal whose evidence basis has since changed. */
  revisit?: boolean;
}

export interface Stage2DecisionSet {
  /** Stage 1 is complete and the screening result is not a decline. */
  available: boolean;
  /** Why the checklist is not yet (or no longer) actionable. */
  unavailableReason?: string;
  /** Changes whenever the evidence behind the proposals changes. */
  basisKey: string;
  /** The Stage 1 report ids the evidence basis rests on. */
  basisReportIds: string[];
  decisions: Stage2Decision[];
  /** Decisions still open (not decided, unknown, deferred or n/a). */
  openCount: number;
  /** Open decisions that shape the scenario, in catalogue order. */
  openScenarioShaping: Stage2DecisionId[];
  /** The decision "Do this next" deep-links to, when one should lead. */
  headline: Stage2DecisionId | null;
}

/** Review state kept on the site document, outside `ProjectIntent`. */
export interface Stage2DecisionReview {
  deferred?: Partial<Record<Stage2DecisionId, { at: string; basisKey: string }>>;
  accepted?: Partial<Record<Stage2DecisionId, {
    at: string;
    basisKey: string;
    reportIds: string[];
    summary: string;
  }>>;
}

/** Open questions carried by Stage 1 findings, normalised. */
export function openQuestionFromClaim(
  claim: FindingClaim,
  reportType: ReportTypeId,
  reportId: string,
): Stage2OpenQuestion | null {
  const question = stripNarrativeArtifacts(claim.openQuestion ?? '').trim();
  if (!question) return null;
  const followUp = stripNarrativeArtifacts(claim.suggestedFollowUp ?? '').trim();
  return {
    question,
    ...(followUp ? { followUp } : {}),
    reportType,
    reportId,
  };
}
